import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Eye, EyeOff, GripVertical, RotateCcw, Settings2 } from "lucide-react";
import { useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export type ColumnDefinition<K extends string> = {
  key: K;
  label: string;
};

// Two visual tones for the trigger gear, mirroring ExportButton:
// - `default` — neutral foreground, for toolbars on a card/neutral background.
// - `primary` — light-on-primary, for use inside the SectionBar's right slot
//   (where the background is the brand color).
type ColumnManagerTone = "default" | "primary";

type ColumnManagerProps<K extends string> = {
  columns: readonly ColumnDefinition<K>[];
  orderedKeys: readonly K[];
  isHidden: (key: K) => boolean;
  alwaysVisible?: readonly K[];
  tone?: ColumnManagerTone;
  onReorder: (next: K[]) => void;
  onToggleHidden: (key: K, hidden: boolean) => void;
  onReset: () => void;
};

// Per-table column visibility + reorder panel. Gear icon in the page's
// panel header opens a popover; the drag area on each row reorders, the
// eye button on the right hides / shows, Reset wipes saved preferences.
//
// Why the two parts of each row are visually separated by a vertical
// rule: dnd-kit's listeners are bound to the drag area only, never to
// the eye button. Without this isolation a click on the eye button can
// be misread as the start of a drag and the click silently drops on the
// floor.
export function ColumnManager<K extends string>({
  columns,
  orderedKeys,
  isHidden,
  alwaysVisible,
  tone = "default",
  onReorder,
  onToggleHidden,
  onReset,
}: ColumnManagerProps<K>) {
  const sensors = useSensors(
    // 8 pixels — enough headroom that a deliberate click on the drag
    // area doesn't accidentally start a drag.
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  // Stable items reference for SortableContext — re-spreading on every
  // render would force dnd-kit to recompute internals unnecessarily.
  const items = useMemo<K[]>(() => [...orderedKeys], [orderedKeys]);
  const lookup = useMemo(() => {
    const m = new Map<K, ColumnDefinition<K>>();
    for (const c of columns) m.set(c.key, c);
    return m;
  }, [columns]);
  const lockedSet = useMemo(() => new Set<string>(alwaysVisible ?? []), [alwaysVisible]);

  function handleDragEnd(e: DragEndEvent) {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIndex = items.indexOf(active.id as K);
    const newIndex = items.indexOf(over.id as K);
    if (oldIndex < 0 || newIndex < 0) return;
    onReorder(arrayMove([...items], oldIndex, newIndex) as K[]);
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label="Manage columns"
          title="Manage columns"
          className={
            tone === "primary"
              ? "size-7 text-primary-foreground/80 hover:bg-primary-foreground/10 hover:text-primary-foreground"
              : "size-7 text-muted-foreground hover:bg-muted hover:text-foreground"
          }
        >
          <Settings2 className="size-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-0">
        <div className="flex items-center justify-between border-b px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          <span>Columns</span>
          <button
            type="button"
            onClick={onReset}
            aria-label="Reset column layout to defaults"
            title="Reset to default column order and visibility"
            className="inline-flex items-center gap-1 rounded font-normal normal-case tracking-normal text-[10px] hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
          >
            <RotateCcw className="size-3" />
            Reset
          </button>
        </div>
        <div className="p-2">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext items={items} strategy={verticalListSortingStrategy}>
              <ul className="space-y-1">
                {items.map((key) => {
                  const def = lookup.get(key);
                  if (!def) return null;
                  const hidden = isHidden(key);
                  const locked = lockedSet.has(key);
                  return (
                    <ColumnRow
                      key={key}
                      columnKey={key}
                      label={def.label}
                      hidden={hidden}
                      locked={locked}
                      onToggleHidden={() => onToggleHidden(key, !hidden)}
                    />
                  );
                })}
              </ul>
            </SortableContext>
          </DndContext>
        </div>
        <div className="border-t px-3 py-1.5 text-[10px] text-muted-foreground">
          Drag the left side of a row to reorder, or focus a row and press space then use the arrow
          keys. Click the eye to hide or show.
        </div>
      </PopoverContent>
    </Popover>
  );
}

function ColumnRow<K extends string>({
  columnKey,
  label,
  hidden,
  locked,
  onToggleHidden,
}: {
  columnKey: K;
  label: string;
  hidden: boolean;
  locked: boolean;
  onToggleHidden: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: columnKey,
  });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  return (
    <li
      ref={setNodeRef}
      style={style}
      className="flex items-stretch rounded border bg-card text-sm"
    >
      {/* Drag area: handle + label. dnd-kit listeners attach ONLY here,
          so a click on the eye button is never misread as the start of
          a drag. */}
      <div
        {...attributes}
        {...listeners}
        className="flex flex-1 cursor-grab touch-none items-center gap-2 px-2 py-1.5 active:cursor-grabbing focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
      >
        <GripVertical className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
        <span className={`flex-1 truncate ${hidden ? "text-muted-foreground line-through" : ""}`}>
          {label}
        </span>
      </div>
      {/* Eye button: completely outside the drag listeners. A plain
          onClick — no propagation gymnastics needed. */}
      <button
        type="button"
        onClick={() => {
          if (locked) return;
          onToggleHidden();
        }}
        aria-label={hidden ? `Show ${label}` : `Hide ${label}`}
        title={locked ? "This column can't be hidden" : hidden ? "Show column" : "Hide column"}
        disabled={locked}
        className="border-l px-3 text-muted-foreground hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40"
      >
        {hidden ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
      </button>
    </li>
  );
}
